import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router, UrlTree } from '@angular/router';
import { ConvertHelper, NavigationInfo } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, timeout } from 'rxjs/operators';
import { WebApiInfo, WebApiSystemInfo } from '../models/webapi-info.model';
import { WebApiEditorComponentInfo } from './../webapi-editor/webapi-editor-component-info';
import { WebApiService } from './webapi.service';

@Injectable({
  providedIn: 'root',
})
export class WebApiInfoService extends WebApiService {
  constructor(
    httpClient: HttpClient,
    abcConfigService: AbcConfigService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    super(httpClient, abcConfigService);
  }

  private getWebApiConfigurationURL(webApiBaseURL: string): string {
    const currentWebApiBaseURL: string = this.abcConfigService.getCleanWebApiBaseURL(
      this.abcConfigService.configuration.WebApi.BaseURL
    );
    webApiBaseURL = this.abcConfigService.getCleanWebApiBaseURL(webApiBaseURL);

    const urlForCurrentWebApi: string = this.abcConfigService.configuration.WebApi.ServiceURLs.Account_GetConfiguration;
    let newConfigurationURL: string = null;

    if (urlForCurrentWebApi) {
      if (currentWebApiBaseURL && urlForCurrentWebApi.startsWith(currentWebApiBaseURL)) {
        newConfigurationURL = urlForCurrentWebApi.replace(currentWebApiBaseURL, webApiBaseURL); // replaces only first (= sufficient)
      } else if (urlForCurrentWebApi.startsWith('api')) {
        newConfigurationURL = `${webApiBaseURL}/${urlForCurrentWebApi}`; // first init
      }
    }

    if (!newConfigurationURL) {
      newConfigurationURL = `${webApiBaseURL}/api/Account/GetConfiguration`; // Last fallback
    }

    return newConfigurationURL;
  }

  public getCurrentWebApiInfo(): Observable<WebApiInfo> {
    return this.getWebApiInfo(this.abcConfigService.configuration.WebApi.BaseURL);
  }

  public getWebApiInfo(baseURL: string): Observable<WebApiInfo> {
    const webApiConfigurationURL: string = this.getWebApiConfigurationURL(baseURL);

    return of(webApiConfigurationURL).pipe(
      concatMap((url) => this.executeWebApiPOST(url, { OutputMode: 'Json' }, true)),
      timeout(10000),
      catchError((err) => {
        // If invalid URL is entered by user
        console.log(err);
        return of(<ArrayBuffer>null);
      }),
      map((response) => {
        if (response) {
          let needsAuthorization: boolean;

          if (this.abcConfigService?.configuration?.WebApi?.AnonymousLogin) {
            needsAuthorization = false; // Config file value has priority only if anonymous login allowed there
          } else {
            needsAuthorization = !ConvertHelper.toBoolean(response['AllowAnonymousAccess']);
          }

          return new WebApiInfo(
            baseURL,
            <string>response['DisplayText'] ?? 'N/A',
            needsAuthorization,
            ConvertHelper.toBoolean(response['UseLocalSecurityContext']),
            ConvertHelper.toBoolean(response['UseRemoteSecurityContext']),
            <string>response['FixedCulture'] ?? 'de',
            <string>response['Version'] ?? 'N/A',
            <WebApiSystemInfo>response['SystemInfo']
          );
        } else {
          // Invalid Base URL
          return WebApiInfo.CreateInvalidWebApiBaseURLInfo(baseURL);
        }
      })
    );
  }

  public isChangingWebApiSupported(router?: Router): boolean {
    return WebApiEditorComponentInfo.isChangingWebApiSupported(router ?? this.router);
  }

  public getWebApiQRImageURL(webApiBaseURL: string): string {
    return `${this.abcConfigService.getCleanWebApiBaseURL(webApiBaseURL)}/qr`;
  }

  public createWebApiEditorPageNavigationInfo(
    router?: Router,
    route?: ActivatedRoute,
    forwardTo?: string
  ): NavigationInfo {
    return WebApiEditorComponentInfo.createWebApiEditorPageNavigationInfo(
      router ?? this.router,
      route ?? this.route,
      forwardTo
    );
  }

  public navigateToWebApiEditorPage(router?: Router, route?: ActivatedRoute, forwardTo?: string): Promise<boolean> {
    return WebApiEditorComponentInfo.navigateToWebApiEditorPage(router ?? this.router, route ?? this.route, forwardTo);
  }

  public createWebApiEditorPageUrlTree(router?: Router, route?: ActivatedRoute, forwardTo?: string): UrlTree {
    return WebApiEditorComponentInfo.createWebApiEditorPageUrlTree(
      router ?? this.router,
      route ?? this.route,
      forwardTo
    );
  }
}
